import {
  TranslationMap,
  TranslationString
} from 'shared/entities/localization';
import { SelectorEntities } from 'shared/newEntities/components/Selector';

import { ChannelKindType, ExternalChannelKind } from '../channels';
import {
  VarBoolValue,
  VarLikedType,
  VarRealmValue,
  VarSpecial,
  VarType
} from '../vars';

export enum ConditionCmp {
  EQ = 'eq',
  NE = 'ne',
  LIKE = 'like',
  STR_IN = 'contains_substr',
  STR_NIN = 'ncontains_substr',
  STR_STARTS = 'startswith',
  STR_ENDS = 'endswith',
  NSTARTS_WITH = 'nstartswith',
  NENDS_WITH = 'nendswith',
  CONTAINS_PHRASE = 'contains_phrase',
  NCONTAINS_PHRASE = 'ncontains_phrase',
  GT = 'gt',
  LT = 'lt',
  LTE = 'lte',
  GTE = 'gte',
  LIST_IN = 'list_in',
  LIST_NIN = 'list_nin'
}

export const mapConditionCmpToItem: Record<
  ConditionCmp,
  {
    id: ConditionCmp;
    title: TranslationString | string;
    shortcut: TranslationString | string;
  }
> = {
  [ConditionCmp.LIKE]: {
    id: ConditionCmp.LIKE,
    title: (t) =>
      t('condition.cmp.like.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.like.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.EQ]: {
    id: ConditionCmp.EQ,
    title: (t) =>
      t('condition.cmp.eq.title', {
        ns: 'entities'
      }),
    shortcut: '='
  },
  [ConditionCmp.NE]: {
    id: ConditionCmp.NE,
    title: (t) =>
      t('condition.cmp.ne.title', {
        ns: 'entities'
      }),
    shortcut: '!='
  },
  [ConditionCmp.STR_IN]: {
    id: ConditionCmp.STR_IN,
    title: (t) =>
      t('condition.cmp.strIn.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.strIn.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.STR_NIN]: {
    id: ConditionCmp.STR_NIN,
    title: (t) =>
      t('condition.cmp.strNIn.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.strNIn.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.STR_STARTS]: {
    id: ConditionCmp.STR_STARTS,
    title: (t) =>
      t('condition.cmp.strStarts.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.strStarts.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.STR_ENDS]: {
    id: ConditionCmp.STR_ENDS,
    title: (t) =>
      t('condition.cmp.strEnds.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.strEnds.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.LIST_IN]: {
    id: ConditionCmp.LIST_IN,
    title: (t) =>
      t('condition.cmp.listIn.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.listIn.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.LIST_NIN]: {
    id: ConditionCmp.LIST_NIN,
    title: (t) =>
      t('condition.cmp.listNIn.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.listNIn.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.GT]: {
    id: ConditionCmp.GT,
    title: '>',
    shortcut: '>'
  },
  [ConditionCmp.GTE]: {
    id: ConditionCmp.GTE,
    title: '≥',
    shortcut: '≥'
  },
  [ConditionCmp.LT]: {
    id: ConditionCmp.LT,
    title: '<',
    shortcut: '<'
  },
  [ConditionCmp.LTE]: {
    id: ConditionCmp.LTE,
    title: '≤',
    shortcut: '≤'
  },
  [ConditionCmp.NSTARTS_WITH]: {
    id: ConditionCmp.NSTARTS_WITH,
    title: (t) =>
      t('condition.cmp.nStartsWith.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.nStartsWith.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.NENDS_WITH]: {
    id: ConditionCmp.NENDS_WITH,
    title: (t) =>
      t('condition.cmp.nEndsWith.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.nEndsWith.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.CONTAINS_PHRASE]: {
    id: ConditionCmp.CONTAINS_PHRASE,
    title: (t) =>
      t('condition.cmp.containsPhrase.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.containsPhrase.title', {
        ns: 'entities'
      })
  },
  [ConditionCmp.NCONTAINS_PHRASE]: {
    id: ConditionCmp.NCONTAINS_PHRASE,
    title: (t) =>
      t('condition.cmp.nContainsPhrase.title', {
        ns: 'entities'
      }),
    shortcut: (t) =>
      t('condition.cmp.nContainsPhrase.title', {
        ns: 'entities'
      })
  }
};

export const commonCmpOrder = [ConditionCmp.EQ, ConditionCmp.NE];

export const numberVarCmpOrder = [
  ConditionCmp.EQ,
  ConditionCmp.NE,
  ConditionCmp.GT,
  ConditionCmp.LT,
  ConditionCmp.LTE,
  ConditionCmp.GTE
];

export const stringVarCmpOrder = [
  ConditionCmp.EQ,
  ConditionCmp.NE,
  ConditionCmp.LIKE,
  ConditionCmp.STR_IN,
  ConditionCmp.STR_NIN,
  ConditionCmp.STR_STARTS,
  ConditionCmp.STR_ENDS,
  ConditionCmp.NSTARTS_WITH,
  ConditionCmp.NENDS_WITH,
  ConditionCmp.CONTAINS_PHRASE,
  ConditionCmp.NCONTAINS_PHRASE
];

export const dateVarCmpOrder = numberVarCmpOrder;

export const mapVarTypeToConditionCmp: Record<VarType, ConditionCmp[]> = {
  [VarType.str]: stringVarCmpOrder,
  [VarType.num]: numberVarCmpOrder,
  [VarType.bool]: commonCmpOrder,
  [VarType.attachment]: commonCmpOrder,
  [VarType.list]: commonCmpOrder,
  [VarType.dict]: commonCmpOrder,
  [VarType.expr]: commonCmpOrder,
  [VarType.date]: dateVarCmpOrder,
  [VarType.datetime]: dateVarCmpOrder
};

export enum ConditionMode {
  AND = 'and',
  OR = 'or'
}

export const conditionModeOrder = [ConditionMode.AND, ConditionMode.OR];

export const mapConditionModeToTitle: TranslationMap<
  ConditionMode,
  TranslationString
> = {
  [ConditionMode.OR]: (t) =>
    t('condition.mode.or', {
      ns: 'entities'
    }),
  [ConditionMode.AND]: (t) =>
    t('condition.mode.and', {
      ns: 'entities'
    })
};

export const mapConditionModeToItem: SelectorEntities<ConditionMode> = {
  [ConditionMode.OR]: {
    id: ConditionMode.OR,
    title: mapConditionModeToTitle[ConditionMode.OR]
  },
  [ConditionMode.AND]: {
    id: ConditionMode.AND,
    title: mapConditionModeToTitle[ConditionMode.AND]
  }
};

export type VarCommonConditionServer = {
  key: string;
  value: string;
  cmp: ConditionCmp;
};

export type VarDateConditionServer = {
  key: string;
  value: string | null;
  cmp: ConditionCmp;
};

export type MessageConditionServer = {
  key: VarSpecial.messageText;
  value: string;
  cmp: ConditionCmp;
};

export type RefConditionServer = {
  key: VarSpecial.ref;
  value: string;
  cmp: ConditionCmp;
};

export type RefSourceConditionServer = {
  key: VarSpecial.refSource;
  value: string;
  cmp: ConditionCmp;
};

export type LikedIdSourceConditionServer = {
  key: VarSpecial.likedId;
  value: string;
  cmp: ConditionCmp;
};

export type LikedTypeSourceConditionServer = {
  key: VarSpecial.likedType;
  value: VarLikedType;
  cmp: ConditionCmp;
};

export type PostIdConditionServer = {
  key: VarSpecial.postId;
  value: string;
  cmp: ConditionCmp;
};

export type BoolConditionServer = {
  key: string;
  value: VarBoolValue;
  cmp: ConditionCmp;
};

export type ChannelKindConditionServer = {
  key: VarSpecial.channelKind;
  value: ChannelKindType;
  cmp: ConditionCmp;
};

export type ChannelConditionServer = {
  key: VarSpecial.channelOid;
  value: string;
  cmp: ConditionCmp;
};

export type RealmConditionServer = {
  key: VarSpecial.realm;
  value: VarRealmValue;
  cmp: ConditionCmp;
};

export type UserConditionServer = {
  key: VarSpecial.userId | VarSpecial.userOid;
  value: string;
  cmp: ConditionCmp;
};

export type VkFormLeadIdConditionServer = {
  key: VarSpecial.vkFormLeadId;
  value: string;
  cmp: ConditionCmp;
};

export type VkFormIdConditionServer = {
  key: VarSpecial.vkFormId;
  value: string;
  cmp: ConditionCmp;
};

export type VkFormNameConditionServer = {
  key: VarSpecial.vkFormName;
  value: string;
  cmp: ConditionCmp;
};

export type VkFormAdIdConditionServer = {
  key: VarSpecial.vkFormAdId;
  value: string;
  cmp: ConditionCmp;
};

export type ExternalChannelKindConditionServer = {
  key: VarSpecial.externalChannelKind;
  value: ExternalChannelKind | '';
  cmp: ConditionCmp;
};

export type VarConditionServer =
  | VarCommonConditionServer
  | VarDateConditionServer
  | MessageConditionServer
  | BoolConditionServer
  | RefConditionServer
  | RefSourceConditionServer
  | LikedIdSourceConditionServer
  | LikedTypeSourceConditionServer
  | PostIdConditionServer
  | ChannelKindConditionServer
  | ChannelConditionServer
  | RealmConditionServer
  | UserConditionServer
  | VkFormLeadIdConditionServer
  | VkFormIdConditionServer
  | VkFormNameConditionServer
  | VkFormAdIdConditionServer;

export interface SqConditionServer {
  expr: string;
}

export type BaseConditionParamsServer = VarConditionsServer &
  SqConditionsServer;

export type SqConditionsServer = {
  sq_conditions: SqConditionServer[];
  sq_conditions_mode: ConditionMode;
};

export type VarConditionsServer = {
  vars_conditions: VarConditionServer[];
  vars_conditions_mode: ConditionMode;
};

export type MessageConditionsServer = {
  message_conditions_mode: ConditionMode;
  message_conditions: MessageConditionServer[];
};

export type ConditionParamsServer = BaseConditionParamsServer &
  MessageConditionsServer;
